import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, getRouteApi } from "@tanstack/react-router";
import { ApiError, getProgram, updateProgram, type EmailTemplate, type Program, type ProgramUpdate, type Template } from "@/api/client";
import { useSession } from "@/auth/session";
import { DetailField, EmptyState, ErrorBanner, WorkspaceHeader } from "@/components/ui";
import { useEmailTemplateChoices } from "@/features/email/choices";
import { ProgramSignatories } from "@/features/programs/ProgramSignatories";
import { ProgramForm } from "@/features/programs/ProgramForm";
import { nextNumber, numberScheme, useTemplateChoices } from "@/features/programs/ProgramList";
import { programFormFrom, toUpdateBody, type ProgramFormValues } from "@/features/programs/programValues";

const detailApi = getRouteApi("/staff/programs/$programId");

export function ProgramDetailPage() {
  const { programId } = detailApi.useParams();
  const program = useQuery({ queryKey: ["program", programId], queryFn: () => getProgram(programId) });

  // 404 is an unknown id, 422 a malformed one; both mean the link points at nothing.
  if (program.error instanceof ApiError && [404, 422].includes(program.error.status)) {
    return (
      <section className="space-y-5">
        <WorkspaceHeader title="Program not found" detail="No program matches this link." />
        <Link to="/programs" className="text-sm font-medium text-zinc-950 underline">
          Back to programs
        </Link>
      </section>
    );
  }
  if (!program.data) {
    return (
      <section className="space-y-5">
        <WorkspaceHeader title="Program" detail={programId} />
        <ErrorBanner error={program.error} />
        {program.isLoading ? <EmptyState title="Loading program" detail="Fetching the program." /> : null}
      </section>
    );
  }
  return <ProgramView program={program.data} />;
}

function ProgramView({ program }: { program: Program }) {
  const { can } = useSession();
  const { templates: loaded, error } = useTemplateChoices();
  // A failed load behaves like no template.read: show ids, offer no select.
  const templates = error ? null : loaded;
  // A failed load hides the select like no certificate.send.
  const { templates: emailLoaded, error: emailError } = useEmailTemplateChoices();
  const emailTemplates = emailError ? null : emailLoaded;
  return (
    <section className="space-y-5">
      <WorkspaceHeader title={program.name} detail="Induction program: numbering, payment and template" />
      {error ? (
        <p role="alert" className="text-sm text-red-700">
          Templates could not be loaded: {error.message}. The template is shown by id until you reload.
        </p>
      ) : null}
      {can("program.manage") ? (
        <EditProgram program={program} templates={templates} emailTemplates={emailTemplates} />
      ) : (
        <ReadOnlyProgram program={program} templates={templates} emailTemplates={emailTemplates} />
      )}
      <ProgramSignatories programId={program.id} />
    </section>
  );
}

function templateName(id: string | null | undefined, templates: Template[] | null) {
  if (!id) return "None";
  return templates?.find((t) => t.id === id)?.name ?? id;
}

function ReadOnlyProgram({
  program,
  templates,
  emailTemplates,
}: {
  program: Program;
  templates: Template[] | null;
  emailTemplates: EmailTemplate[] | null;
}) {
  const { archivedName } = useEmailTemplateChoices();
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4">
      <dl className="grid gap-3 text-sm md:grid-cols-3">
        <DetailField label="Type" value={program.program_type} />
        <DetailField label="Induction date" value={program.induction_date ?? "No date"} />
        <DetailField label="Numbering" value={numberScheme(program)} />
        <DetailField label="Next number" value={nextNumber(program)} mono />
        <DetailField label="Payment" value={program.requires_payment ? "Required" : "Not required"} />
        <DetailField label="Template" value={templateName(program.template_id, templates)} />
        {emailTemplates ? (
          <DetailField
            label="Email template"
            value={
              program.email_template_id
                ? (emailTemplates.find((t) => t.id === program.email_template_id)?.name ?? archivedName(program.email_template_id))
                : "System default"
            }
          />
        ) : null}
      </dl>
    </div>
  );
}

function EditProgram({
  program,
  templates,
  emailTemplates,
}: {
  program: Program;
  templates: Template[] | null;
  emailTemplates: EmailTemplate[] | null;
}) {
  const queryClient = useQueryClient();
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: (body: ProgramUpdate) => updateProgram(program.id, body),
    onSuccess: async (next) => {
      setSaved(true);
      // A refetch begun before the save would otherwise land after it with the old values.
      await queryClient.cancelQueries({ queryKey: ["program", program.id] });
      queryClient.setQueryData(["program", program.id], next);
      await queryClient.invalidateQueries({ queryKey: ["programs"] });
    },
  });

  const notes = (values: ProgramFormValues) => (
    <div className="space-y-1 text-xs text-zinc-600">
      {values.requires_payment !== program.requires_payment ? (
        <p>
          {values.requires_payment
            ? "Batches of this program will need a confirmed payment for every recipient before they can be approved."
            : "Batches of this program will no longer need confirmed payments before they can be approved."}
        </p>
      ) : null}
      {values.template_id !== (program.template_id ?? "") ? (
        <p>
          {values.template_id
            ? `New batches for this program will issue with ${templateName(values.template_id, templates)}'s current version.`
            : "Without a template, new batches can't be started for this program."}
        </p>
      ) : null}
    </div>
  );

  return (
    <div className="space-y-4 rounded-lg border border-zinc-200 bg-white p-4">
      <h2 className="text-sm font-semibold">Program</h2>
      {templates === null ? (
        <p className="text-xs text-zinc-500">Template: {program.template_id ?? "none"}</p>
      ) : null}
      <ProgramForm
        // Saved values reset the form, so the next edit starts from what the server holds.
        key={JSON.stringify(program)}
        initial={programFormFrom(program)}
        floor={program.membership_no_next ?? 1}
        templates={templates}
        emailTemplates={emailTemplates}
        numberLabel="Next number"
        numberHint="Can only go up; lower numbers may already be issued."
        notes={notes}
        isUnchanged={(values) => Object.keys(toUpdateBody(values, program)).length === 0}
        submitLabel="Save changes"
        pendingLabel="Saving..."
        pending={save.isPending}
        error={save.error}
        onEdit={() => {
          setSaved(false);
          if (save.error) save.reset();
        }}
        onSubmit={(values) => save.mutate(toUpdateBody(values, program))}
      />
      {saved ? (
        <p role="status" className="text-sm font-medium text-emerald-700">
          Saved.
        </p>
      ) : null}
    </div>
  );
}
